import React, { useState, useEffect } from "react";
import {
  Shield,
  Plus,
  Calendar,
  DollarSign,
  Clock,
  Building2,
  Trash2,
  Edit2,
  X,
  Save,
} from "lucide-react";
import apiService from "../services/api";
import "../styles/Enforcement.css";

const Enforcement = () => {
  const [enforcements, setEnforcements] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedSite, setSelectedSite] = useState("");
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showMonthModal, setShowMonthModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedCustomerName, setSelectedCustomerName] = useState("");

  // Get unique customer names
  const uniqueCustomerNames = [...new Set(customers.map((c) => c.name))]
    .filter(Boolean)
    .sort();

  // Get sites for selected customer
  const sitesForCustomer = customers.filter(
    (c) => c.name === selectedCustomerName,
  );

  // Form state
  const [formData, setFormData] = useState({
    customer_id: "",
    employee_name: "",
    work_date: "",
    hours: "",
    hour_rate: "",
    notes: "",
  });

  useEffect(() => {
    loadCustomers();
    loadAvailableMonths();
  }, []);

  useEffect(() => {
    if (selectedMonth) {
      loadEnforcements();
      loadDashboard();
    }
  }, [selectedMonth]);

  const loadCustomers = async () => {
    try {
      const response = await apiService.getCustomers({ limit: 1000 });
      setCustomers(response.customers || []);
    } catch (error) {
      console.error("Failed to load customers:", error);
    }
  };

  const loadAvailableMonths = async () => {
    try {
      // Load distinct months from presence data
      const response = await apiService.getPresenceAvailableMonths();
      if (response.success && response.months && response.months.length > 0) {
        setAvailableMonths(response.months);
        // Don't auto-select, show modal first
        if (!selectedMonth) {
          setShowMonthModal(true);
        }
      } else {
        // Fallback to enforcement months
        const enfResponse = await apiService.getEnforcementMonths();
        if (enfResponse.success && enfResponse.months.length > 0) {
          setAvailableMonths(enfResponse.months);
          if (!selectedMonth) {
            setShowMonthModal(true);
          }
        } else {
          // Set current month as default option
          const currentMonth = new Date().toISOString().slice(0, 7);
          setAvailableMonths([currentMonth]);
          setShowMonthModal(true);
        }
      }
    } catch (error) {
      console.error("Failed to load months:", error);
      const currentMonth = new Date().toISOString().slice(0, 7);
      setAvailableMonths([currentMonth]);
      setShowMonthModal(true);
    }
  };

  const loadEnforcements = async () => {
    try {
      setLoading(true);
      const response = await apiService.getEnforcements(selectedMonth);
      if (response.success) {
        setEnforcements(response.data || []);
      }
    } catch (error) {
      console.error("Failed to load enforcements:", error);
      alert("שגיאה בטעינת נתוני אכיפה");
    } finally {
      setLoading(false);
    }
  };

  const loadDashboard = async () => {
    try {
      const response = await apiService.getEnforcementDashboard(selectedMonth);
      if (response.success) {
        setDashboard(response);
      }
    } catch (error) {
      console.error("Failed to load dashboard:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const calculateTotal = () => {
    const hours = parseFloat(formData.hours) || 0;
    const rate = parseFloat(formData.hour_rate) || 0;
    return (hours * rate).toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.customer_id ||
      !formData.work_date ||
      !formData.hours ||
      !formData.hour_rate
    ) {
      alert("נא למלא את כל השדות הנדרשים");
      return;
    }

    try {
      if (editingId) {
        await apiService.updateEnforcement(editingId, formData);
        alert("רשומת אכיפה עודכנה בהצלחה");
      } else {
        await apiService.createEnforcement(formData);
        alert("רשומת אכיפה נוצרה בהצלחה");
      }

      resetForm();
      loadEnforcements();
      loadDashboard();
      loadAvailableMonths();
    } catch (error) {
      console.error("Failed to save enforcement:", error);
      alert("שגיאה בשמירת רשומת אכיפה");
    }
  };

  const handleEdit = (enforcement) => {
    setFormData({
      customer_id: enforcement.customer_id,
      employee_name: enforcement.employee_name || "",
      work_date: enforcement.work_date,
      hours: enforcement.hours,
      hour_rate: enforcement.hour_rate,
      notes: enforcement.notes || "",
    });
    setEditingId(enforcement.id);
    setShowAddModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("האם אתה בטוח שברצונך למחוק רשומה זו?")) {
      return;
    }

    try {
      await apiService.deleteEnforcement(id);
      alert("רשומת אכיפה נמחקה בהצלחה");
      loadEnforcements();
      loadDashboard();
    } catch (error) {
      console.error("Failed to delete enforcement:", error);
      alert("שגיאה במחיקת רשומת אכיפה");
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: "",
      employee_name: "",
      work_date: "",
      hours: "",
      hour_rate: "",
      notes: "",
    });
    setSelectedCustomerName("");
    setEditingId(null);
    setShowAddModal(false);
  };

  const formatMonth = (monthStr) => {
    if (!monthStr) return "";
    const [year, month] = monthStr.split("-");
    const monthNames = [
      "ינואר",
      "פברואר",
      "מרץ",
      "אפריל",
      "מאי",
      "יוני",
      "יולי",
      "אוגוסט",
      "ספטמבר",
      "אוקטובר",
      "נובמבר",
      "דצמבר",
    ];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("he-IL");
  };

  const getFilteredEnforcements = () => {
    return enforcements.filter((enf) => {
      if (selectedSite && enf.site_name !== selectedSite) {
        return false;
      }
      return true;
    });
  };

  const filteredEnforcements = getFilteredEnforcements();

  return (
    <div className='enforcement-container'>
      <div className='enforcement-header'>
        <div className='header-content'>
          <h1>
            <Shield className='header-icon' />
            ניהול תגבור
          </h1>
          <p className='header-subtitle'>רישום ומעקב אחר שעות תגבור באתרים</p>
        </div>

        <div className='header-actions'>
          <button
            className='month-select-btn'
            onClick={() => setShowMonthModal(true)}
          >
            <Calendar size={18} />
            {selectedMonth ? formatMonth(selectedMonth) : "בחר חודש"}
          </button>

          <div className='month-selector'>
            <Building2 size={18} />
            <select
              value={selectedSite}
              onChange={(e) => setSelectedSite(e.target.value)}
            >
              <option value=''>כל האתרים</option>
              {[
                ...new Set(
                  customers.map((c) => c.site_name || c.name).filter(Boolean),
                ),
              ]
                .sort()
                .map((site) => (
                  <option key={site} value={site}>
                    {site}
                  </option>
                ))}
            </select>
          </div>

          <button className='add-btn' onClick={() => setShowAddModal(true)}>
            <Plus size={18} />
            הוסף רשומה
          </button>
        </div>
      </div>

      {/* Dashboard */}
      {dashboard && (
        <div className='dashboard-cards'>
          <div className='dashboard-card'>
            <div
              className='card-icon'
              style={{ background: "#dbeafe", color: "#3b82f6" }}
            >
              <Clock size={24} />
            </div>
            <div className='card-content'>
              <p className='card-label'>סה״כ שעות</p>
              <h3 className='card-value'>{dashboard.total_hours.toFixed(2)}</h3>
            </div>
          </div>

          <div className='dashboard-card'>
            <div
              className='card-icon'
              style={{ background: "#dcfce7", color: "#10b981" }}
            >
              <DollarSign size={24} />
            </div>
            <div className='card-content'>
              <p className='card-label'>סה״כ לתשלום</p>
              <h3 className='card-value'>
                ₪
                {dashboard.total_amount.toLocaleString("he-IL", {
                  maximumFractionDigits: 0,
                })}
              </h3>
            </div>
          </div>

          <div className='dashboard-card'>
            <div
              className='card-icon'
              style={{ background: "#fef3c7", color: "#f59e0b" }}
            >
              <Building2 size={24} />
            </div>
            <div className='card-content'>
              <p className='card-label'>רשומות</p>
              <h3 className='card-value'>{dashboard.total_records}</h3>
            </div>
          </div>
        </div>
      )}

      {/* Site Statistics */}
      {dashboard && dashboard.sites && dashboard.sites.length > 0 && (
        <div className='site-stats'>
          <h3>פירוט לפי אתר</h3>
          <div className='site-stats-grid'>
            {dashboard.sites.map((site, index) => (
              <div key={index} className='site-stat-card'>
                <div className='site-name'>{site.site_name}</div>
                <div className='site-details'>
                  <div className='site-detail'>
                    <Clock size={16} />
                    <span>{site.total_hours.toFixed(2)} שעות</span>
                  </div>
                  <div className='site-detail'>
                    <DollarSign size={16} />
                    <span>
                      ₪
                      {site.total_amount.toLocaleString("he-IL", {
                        maximumFractionDigits: 0,
                      })}
                    </span>
                  </div>
                  <div className='site-detail'>
                    <span className='record-count'>
                      {site.record_count} רשומות
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Enforcements Table */}
      <div className='enforcements-table-container'>
        {loading ? (
          <div className='loading-state'>
            <div className='spinner'></div>
            <p>טוען נתונים...</p>
          </div>
        ) : filteredEnforcements.length === 0 ? (
          <div className='empty-state'>
            <Shield size={48} />
            <h3>אין רשומות תגבור</h3>
            <p>לא נמצאו רשומות תגבור לחודש {formatMonth(selectedMonth)}</p>
          </div>
        ) : (
          <table className='enforcements-table'>
            <thead>
              <tr>
                <th>תאריך</th>
                <th>אתר</th>
                <th>לקוח</th>
                <th>שם עובד</th>
                <th>שעות</th>
                <th>תעריף</th>
                <th>סה״כ</th>
                <th>הערות</th>
                <th>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {filteredEnforcements.map((enf) => (
                <tr key={enf.id}>
                  <td>{formatDate(enf.work_date)}</td>
                  <td>{enf.site_name || "-"}</td>
                  <td>{enf.customer_name || "-"}</td>
                  <td>{enf.employee_name || "-"}</td>
                  <td className='hours-cell'>{enf.hours.toFixed(2)}</td>
                  <td className='rate-cell'>₪{enf.hour_rate.toFixed(2)}</td>
                  <td className='total-cell'>₪{enf.total_amount.toFixed(2)}</td>
                  <td className='notes-cell'>{enf.notes || "-"}</td>
                  <td className='actions-cell'>
                    <button
                      className='action-btn edit-btn'
                      onClick={() => handleEdit(enf)}
                      title='ערוך'
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className='action-btn delete-btn'
                      onClick={() => handleDelete(enf.id)}
                      title='מחק'
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className='modal-overlay' onClick={resetForm}>
          <div className='modal-content' onClick={(e) => e.stopPropagation()}>
            <div className='modal-header'>
              <h2>{editingId ? "ערוך רשומת תגבור" : "הוסף רשומת תגבור"}</h2>
              <button className='close-btn' onClick={resetForm}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className='enforcement-form'>
              <div className='form-row'>
                <div className='form-group'>
                  <label>
                    לקוח <span className='required'>*</span>
                  </label>
                  <select
                    value={selectedCustomerName}
                    onChange={(e) => {
                      setSelectedCustomerName(e.target.value);
                      setFormData((prev) => ({ ...prev, customer_id: "" }));
                    }}
                    required
                  >
                    <option value=''>בחר לקוח</option>
                    {uniqueCustomerNames.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className='form-group'>
                  <label>
                    אתר <span className='required'>*</span>
                  </label>
                  <select
                    name='customer_id'
                    value={formData.customer_id}
                    onChange={handleInputChange}
                    disabled={!selectedCustomerName}
                    required
                  >
                    <option value=''>בחר אתר</option>
                    {sitesForCustomer.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.site_name || "משרד ראשי"}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className='form-group'>
                <label>שם עובד (אופציונלי)</label>
                <input
                  type='text'
                  name='employee_name'
                  value={formData.employee_name}
                  onChange={handleInputChange}
                  placeholder='הזן שם עובד'
                />
              </div>

              <div className='form-row'>
                <div className='form-group'>
                  <label>
                    תאריך <span className='required'>*</span>
                  </label>
                  <input
                    type='date'
                    name='work_date'
                    value={formData.work_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className='form-group'>
                  <label>
                    שעות <span className='required'>*</span>
                  </label>
                  <input
                    type='number'
                    name='hours'
                    value={formData.hours}
                    onChange={handleInputChange}
                    step='0.01'
                    min='0'
                    placeholder='0.00'
                    required
                  />
                </div>

                <div className='form-group'>
                  <label>
                    תעריף לשעה <span className='required'>*</span>
                  </label>
                  <input
                    type='number'
                    name='hour_rate'
                    value={formData.hour_rate}
                    onChange={handleInputChange}
                    step='0.01'
                    min='0'
                    placeholder='0.00'
                    required
                  />
                </div>
              </div>

              {formData.hours && formData.hour_rate && (
                <div className='calculated-total'>
                  <strong>סה״כ לתשלום:</strong> ₪{calculateTotal()}
                </div>
              )}

              <div className='form-group'>
                <label>הערות</label>
                <textarea
                  name='notes'
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows='3'
                  placeholder='הערות נוספות...'
                />
              </div>

              <div className='form-actions'>
                <button
                  type='button'
                  className='cancel-btn'
                  onClick={resetForm}
                >
                  ביטול
                </button>
                <button type='submit' className='submit-btn'>
                  <Save size={18} />
                  {editingId ? "עדכן" : "שמור"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Month Selection Modal */}
      {showMonthModal && (
        <div
          className='modal-overlay'
          onClick={() => selectedMonth && setShowMonthModal(false)}
        >
          <div
            className='modal-content month-modal'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='modal-header'>
              <h2>
                <Calendar size={24} />
                בחר חודש
              </h2>
              {selectedMonth && (
                <button
                  className='close-btn'
                  onClick={() => setShowMonthModal(false)}
                >
                  <X size={20} />
                </button>
              )}
            </div>

            <div className='month-grid'>
              {availableMonths.map((month) => (
                <button
                  key={month}
                  className={`month-option ${selectedMonth === month ? "selected" : ""}`}
                  onClick={() => {
                    setSelectedMonth(month);
                    setShowMonthModal(false);
                  }}
                >
                  <Calendar size={20} />
                  <span>{formatMonth(month)}</span>
                </button>
              ))}
            </div>

            <div className='modal-footer'>
              <p className='month-hint'>
                בחר חודש להצגת נתוני תגבור או להוספת רשומות חדשות
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Enforcement;
